import { ReactElement } from "react";
import { TileRenderState } from "./RenderState/common";
import { AppType } from "client/Api/Apps/common";
import { TileContentDependencies } from "./useTileContentDependencies";
import { OfficeTileContent } from "./Tiles/OfficeTileContent";
import { UnknownTileContent } from "./Tiles/UnknownTileContent";
import { ShortcutTileContent } from "./Tiles/ShortcutTileContent";
import { EmailTileContent } from "./Tiles/Email/EmailTileContent";
import { DocumentsTileContent } from "./Tiles/Documents/DocumentsTileContent";
import { SkypeTileContent } from "./Tiles/SkypeTileContent";
import { PeopleTileContent } from "./Tiles/PeopleTileContent";
import { CalendarTileContent } from "./Tiles/Calendar/CalendarTileContent";
import { CitrixXenAppTileContent } from "./Tiles/CitrixXenAppTileContent";
import { RssTileContent } from "./Tiles/Rss/RssTileContent";
import { AzureAppTileContent } from "./Tiles/AzureAppTileContent";
import { ClientlessRdpTileContent } from "./Tiles/ClientlessRdpTileContent";
import { EmbedAppTileContent } from "./Tiles/Embed/EmbedAppTileContent";
import { PowerBiTileContent } from "./Tiles/PowerBi/PowerBiTileContent";
import { YammerTileContent } from "./Tiles/Yammer/YammerTileContent";
import { ToDoTileContent } from "./Tiles/ToDo/ToDoTileContent";
import { GoogleTileContent } from "./Tiles/Google/GoogleTileContent";
import { TopDeskTileContent } from "./Tiles/TopDesk/TopDeskTileContent";
import { ConnectWiseTileContent } from "./Tiles/ConnectWise/ConnectWiseTileContent";
import { TeamsTileContent } from "./Tiles/Teams/TeamsTileContent";
import { SharePointTileContent } from "./Tiles/SharePoint/SharePointTileContent";
import { AddressBookTileContent } from "./Tiles/AddressBook/AddressBookTileContent";
import { HowToTileContent } from "./Tiles/howToTile/HowToTileContent";
import { HubTileContent } from "./Tiles/Hub/HubTileContent";
import { LocalAppTileContent } from "./Tiles/LocalAppTileContent";
import { WindowsVirtualDesktopTileContent } from "./Tiles/WindowsVirtualDesktopAppTileContent";
import { VilansKickTileContent } from "./Tiles/VilansKick/VilansKickTileContent";
import { BirthdayTileContent } from "./Tiles/Birthday/BirthdayTileContent";
import { AutotaskTileContent } from "./Tiles/Autotask/AutotaskTileContent";
import { YouTubeTileContent } from "./Tiles/YouTube/YouTubeTileContent";
import { NedapOnsTileContent } from "./Tiles/NedapOns/NedapOnsTileContent";
import { AfasTileContent } from "./Tiles/Afas/AfasTileContent";
import { ZaaksysteemTileContent } from "./Tiles/Zaaksysteem/ZaaksysteemTileContent";
import { ZermeloTileContent } from "./Tiles/Zermelo/ZermeloTileContent";
import { ZenyaDocTileContent } from "./Tiles/ZenyaDoc/ZenyaDocTileContent";
import { LiquitTileContent } from "./Tiles/Liquit/LiquitTileContent";
import { MagisterTileContent } from "./Tiles/Magister/MagisterTileContent";
import { VmwareHorizonAppTileContent } from "./Tiles/VmwareHorizonAppTileContent";
import { TimeZone } from "client/DateAndTime/timezones";
import { IntegrationTileContent } from "./Tiles/Integration/IntegrationTileContent";
import { Coordinate } from "client/Misc/commonTypes";

export type TileContentProps = {
	renderState: TileRenderState;
	dependencies: TileContentDependencies;
	userTimezone: TimeZone;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const TileContent = (props: TileContentProps): ReactElement => {
	const {
		renderState,
		dependencies,
		userTimezone,
		onEdit,
		onOpenTileMenu
	} = props;

	const commonProps: CommonProps = {
		userTimezone,
		onEdit,
		onOpenTileMenu
	};

	return appTypeToComponentFactory[renderState.data.type](
		// TS cannot follow the flow here to understand the resolved component factory matches
		// the renderState
		renderState as any,
		commonProps,
		dependencies
	);
};
TileContent.displayName = "TileContent";

type CommonProps = {
	readonly userTimezone: TimeZone;
	readonly onEdit: (() => void) | undefined;
	readonly onOpenTileMenu: (coordinate: Coordinate) => void;
};

type ComponentFactory<T extends AppType> = (
	renderState: TileRenderState<T>,
	commonProps: CommonProps,
	dependencies: TileContentDependencies
) => ReactElement;

const appTypeToComponentFactory: { [k in AppType]: ComponentFactory<k>; } = {
	[AppType.Unknown]: (renderState, commonProps) => (
		<UnknownTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Url]: (renderState, commonProps) => (
		<ShortcutTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Email]: (renderState, commonProps, dependencies) => (
		<EmailTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.emailLiveInformationProvider}
		/>
	),
	[AppType.Documents]: (renderState, commonProps, dependencies) => (
		<DocumentsTileContent
			{...commonProps}
			renderState={renderState}
			documentsLiveInformationProvider={dependencies.documentsLiveInformationProvider}
			bookmarksProvider={dependencies.bookmarksProvider}
		/>
	),
	[AppType.Office365Excel]: (renderState, commonProps) => (
		<OfficeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365OneNote]: (renderState, commonProps) => (
		<OfficeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365Word]: (renderState, commonProps) => (
		<OfficeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365PowerPoint]: (renderState, commonProps) => (
		<OfficeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365LyncMeeting]: (renderState, commonProps) => (
		<SkypeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365PeopleScreen]: (renderState, commonProps) => (
		<PeopleTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Office365Calendar]: (renderState, commonProps, dependencies) => (
		<CalendarTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.calendarLiveInformationProvider}
		/>
	),
	[AppType.CitrixXenApp]: (renderState, commonProps) => (
		<CitrixXenAppTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Rss]: (renderState, commonProps, dependencies) => (
		<RssTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.rssLiveInformationProvider}
		/>
	),
	[AppType.AzureApp]: (renderState, commonProps) => (
		<AzureAppTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.ClientlessRdp]: (renderState, commonProps) => (
		<ClientlessRdpTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.PowerBi]: (renderState, commonProps) => (
		<PowerBiTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Yammer]: (renderState, commonProps) => (
		<YammerTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Embed]: (renderState, commonProps) => (
		<EmbedAppTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.ToDo]: (renderState, commonProps, dependencies) => (
		<ToDoTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.toDoLiveInformationProvider}
		/>
	),
	[AppType.Google]: (renderState, commonProps) => (
		<GoogleTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.TopDesk]: (renderState, commonProps, dependencies) => (
		<TopDeskTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.topDeskLiveInformationProvider}
		/>
	),
	[AppType.ConnectWise]: (renderState, commonProps, dependencies) => (
		<ConnectWiseTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.connectWiseLiveInformationProvider}
		/>
	),
	[AppType.Teams]: (renderState, commonProps, dependencies) => (
		<TeamsTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.teamsProvider}
		/>
	),
	[AppType.SharePoint]: (renderState, commonProps, dependencies) => (
		<SharePointTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.sharePointProvider}
		/>
	),
	[AppType.AddressBook]: (renderState, commonProps, dependencies) => (
		<AddressBookTileContent
			{...commonProps}
			renderState={renderState}
			liveSettings={dependencies.addressBookSettings}
		/>
	),
	[AppType.HowTo]: (renderState, commonProps, dependencies) => (
		<HowToTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.howToLiveInformationProvider}
		/>
	),
	[AppType.Hub]: (renderState, commonProps, dependencies) => (
		<HubTileContent
			{...commonProps}
			renderState={renderState}
			announcementsProvider={dependencies.announcementsProvider}
			knowledgeItemProvider={dependencies.knowledgeItemProvider}
			eventsProvider={dependencies.eventsProvider}
			communityProvider={dependencies.communityProvider}
		/>
	),
	[AppType.LocalApp]: (renderState, commonProps) => (
		<LocalAppTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.WindowsVirtualDesktop]: (renderState, commonProps) => (
		<WindowsVirtualDesktopTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.VilansKick]: (renderState, commonProps) => (
		<VilansKickTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Birthday]: (renderState, commonProps) => (
		<BirthdayTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Autotask]: (renderState, commonProps, dependencies) => (
		<AutotaskTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.autotaskLiveInformationProvider}
		/>
	),
	[AppType.YouTube]: (renderState, commonProps) => (
		<YouTubeTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.NedapOns]: (renderState, commonProps, dependencies) => (
		<NedapOnsTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.nedapOnsLiveInformationProvider}
		/>
	),
	[AppType.Afas]: (renderState, commonProps, dependencies) => (
		<AfasTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.afasLiveInformationProvider}
		/>
	),
	[AppType.Zaaksysteem]: (renderState, commonProps, dependencies) => (
		<ZaaksysteemTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.zaaksysteemLiveInformationProvider}
		/>
	),
	[AppType.Zermelo]: (renderState, commonProps, dependencies) => (
		<ZermeloTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.zermeloLiveInformationProvider}
		/>
	),
	[AppType.ZenyaDoc]: (renderState, commonProps, dependencies) => (
		<ZenyaDocTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.zenyaDocLiveInformationProvider}
		/>
	),
	[AppType.Liquit]: (renderState, commonProps) => (
		<LiquitTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.VmwareHorizonApp]: (renderState, commonProps) => (
		<VmwareHorizonAppTileContent
			{...commonProps}
			renderState={renderState}
		/>
	),
	[AppType.Magister]: (renderState, commonProps, dependencies) => (
		<MagisterTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.magisterLiveInformationProvider}
		/>
	),
	[AppType.Integration]: (renderState, commonProps, dependencies) => (
		<IntegrationTileContent
			{...commonProps}
			renderState={renderState}
			provider={dependencies.integrationLiveInformationProvider}
		/>
	)
};
